/**
 * ServiceCostWidget
 * Dashboard-widget med 6-månaders servicekostnadstrend per kategori.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { CurrencyDollarIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '@/store/languageStore';
import { serviceApi, CostReportRow } from '@/services/service/serviceApi';

const CATEGORY_COLORS: Record<string, string> = {
  TIRE: '#3b82f6',
  MAINTENANCE: '#f59e0b',
  INSPECTION: '#8b5cf6',
  CUSTOM: '#6b7280',
};

const ServiceCostWidget: React.FC<{ onRemove?: () => void }> = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<CostReportRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const toDate = new Date().toISOString().split('T')[0];
      const fromDate = new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const data = await serviceApi.getCostReport(fromDate, toDate);
      setRows(data);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status !== 404) console.error('ServiceCostWidget load failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const months = Array.from(new Set(rows.map(r => r.month))).sort().slice(-6);
  const categories = Array.from(new Set(rows.map(r => r.category)));
  const grandTotal = rows.reduce((s, r) => s + r.totalSek, 0);

  const monthData = months.map(m => ({
    month: m,
    total: rows.filter(r => r.month === m).reduce((s, r) => s + r.totalSek, 0),
    byCategory: Object.fromEntries(categories.map(c => [c, rows.find(r => r.month === m && r.category === c)?.totalSek || 0])),
  }));

  const maxTotal = Math.max(...monthData.map(m => m.total), 1);

  return (
    <div className="bg-bgCard border border-borderDefault rounded-xl shadow-sm overflow-hidden">
      <div className="px-4 py-3 flex items-center justify-between border-b border-borderDefault">
        <div className="flex items-center gap-2">
          <CurrencyDollarIcon className="w-5 h-5 text-primary" />
          <h3 className="text-sm font-semibold text-textDefault">{t('service.costs.grandTotal')}</h3>
        </div>
        <button onClick={load} disabled={loading} className="text-textTertiary hover:text-textDefault">
          <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="p-4">
        {loading ? (
          <div className="flex justify-center py-6">
            <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-6 text-textTertiary text-sm">
            {t('service.costs.noData')}
          </div>
        ) : (
          <>
            <div className="text-2xl font-bold text-textDefault mb-3">
              {grandTotal.toLocaleString('sv-SE', { maximumFractionDigits: 0 })} kr
            </div>

            {/* Mini bar chart */}
            <div className="flex items-end gap-1.5 h-20 mb-3">
              {monthData.map(m => (
                <div key={m.month} className="flex-1 flex flex-col-reverse gap-0.5" style={{ height: '5rem' }}>
                  {categories.map(cat => {
                    const val = m.byCategory[cat] || 0;
                    const pct = maxTotal > 0 ? (val / maxTotal) * 100 : 0;
                    if (pct < 0.5) return null;
                    return (
                      <div
                        key={cat}
                        title={`${cat}: ${val.toLocaleString('sv-SE')} kr`}
                        style={{ height: `${pct}%`, backgroundColor: CATEGORY_COLORS[cat] || '#6b7280' }}
                        className="w-full rounded-t"
                      />
                    );
                  })}
                </div>
              ))}
            </div>

            {/* Month labels */}
            <div className="flex gap-1.5 mb-3">
              {monthData.map(m => (
                <div key={m.month} className="flex-1 text-xs text-center text-textTertiary">
                  {m.month.substring(5)}
                </div>
              ))}
            </div>

            {/* Category legend */}
            <div className="flex flex-wrap gap-2">
              {categories.map(cat => {
                const catTotal = rows.filter(r => r.category === cat).reduce((s, r) => s + r.totalSek, 0);
                return (
                  <div key={cat} className="flex items-center gap-1 text-xs text-textSecondary">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CATEGORY_COLORS[cat] || '#6b7280' }} />
                    <span>{t(`service.category.${cat.toLowerCase()}`)}</span>
                    <span className="font-medium text-textDefault">{catTotal.toLocaleString('sv-SE', { maximumFractionDigits: 0 })} kr</span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ServiceCostWidget;
